<script>
import { mapState, mapActions } from 'vuex';
import SkistarResortsSelect from '../../../components/ski/skistar/SkistarResortsSelect';
import SkistarService from '../../../services/ski/skistar-service';

export default {
  name: 'SkistarResortUnhandledImages',
  components: {
    SkistarResortsSelect,
  },
  data() {
    return {
      loading: true,
      images: {},
      totalCount: 0,
      subAreas: [],
      selectedSubAreaId: null,
    };
  },
  computed: {
    ...mapState({
      resortId: state => state.skistar.resorts.resort,
      webservice: state => state.skistar.resorts.webservice,
      webservices: state => state.skistar.resorts.webservices,
      resortsLoading: state => state.skistar.resorts.itemsLoading,
    }),
    activeImages() {
      return Object.values(this.images).filter(image => image.active);
    },
    deactivatedImages() {
      return Object.values(this.images).filter(image => !image.active);
    },
  },
  watch: {
    webservice() {
      this.loadImages();
    },
    resortId() {
      this.loadSubAreas();
    },
    selectedSubAreaId() {
      this.loadImages();
    },
  },
  mounted() {
    this.loading = false;
    this.loadSubAreas();
    if (this.webservice) {
      this.loadImages();
    }
  },
  methods: {
    ...mapActions({
      setWebservice: 'skistar/setWebservice',
    }),
    loadSubAreas() {
      if (!this.resortId) return;

      this.loading = true;
      SkistarService.getResortSubareas(this.resortId)
        .then(res => {
          if (res.data) {
            this.subAreas = res.data;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    loadTotalCount() {
      if (!this.webservice) return;
      SkistarService.getTotalUnhandledImagesByWebservice(this.webservice, { subAreaId: this.selectedSubAreaId })
        .then(res => {
          if (res.data) {
            this.totalCount = res.data.total;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    loadImages() {
      if (!this.webservice) return;

      this.loading = true;
      this.images = {};
      const limit = this.selectedSubAreaId ? 500 : 100;
      SkistarService.getUnhandledImagesByWebservice(this.webservice, limit, { subAreaId: this.selectedSubAreaId })
        .then(res => {
          if (res.status === 200 && res.data) {
            const images = {};
            res.data.forEach(image => {
              images[image.identifier] = {
                ...image,
                active: true,
                imageUrlSmall: image.imageUrl.replace('ImageSize=Original', 'ImageSize=Small'),
              };
            });
            this.images = images;
          }
          this.loadTotalCount();
        })
        .catch(() => {
          this.loading = false;
        });
    },
    updateImages() {
      this.loading = true;
      const activatedImageIdentifiers = Object.values(this.images).filter(image => image.active).map(image => image.identifier);
      const deactivatedImageIdentifiers = Object.values(this.images).filter(image => !image.active).map(image => image.identifier);
      SkistarService.updateUnhandledImages({ activatedImageIdentifiers, deactivatedImageIdentifiers })
        .then(res => {
          if (res.status === 204) {
            this.loading = false;
            this.loadImages();
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    deactivateImage(imageIdentifier) {
      this.images[imageIdentifier].active = false;
    },
    handleRestoreImage(imageIdentifier) {
      this.images[imageIdentifier].active = true;
    },
  },
};
</script>

<template>
  <v-card>
    <div v-if="loading" class="min-height">
      <v-progress-circular indeterminate/>
    </div>
    <v-container v-else fluid>
      <v-layout row wrap>
        <v-flex xs2>
          <skistar-resorts-select />
        </v-flex>
        <v-flex xs3 pl-4>
          <v-select
            :value="webservice"
            :items="webservices"
            :loading="resortsLoading"
            :disabled="resortsLoading"
            label="Choose webservice"
            @change="setWebservice"
          />
        </v-flex>
        <v-flex xs4 pl-4>
          <v-autocomplete
            v-model="selectedSubAreaId"
            :items="subAreas"
            :loading="loading"
            :disabled="loading"
            label="Choose Subarea"
            item-text="name"
            item-value="id"
            clearable
          />
        </v-flex>
      </v-layout>

      <v-container v-if="!loading && webservice && !activeImages.length && !deactivatedImages.length" grid-list-xs fluid>
        <v-layout
          row
          justify-center
          align-center
          class="no-images-message"
        >
          <v-flex xs12 style="text-align: center">
            <h3>
              {{ selectedSubAreaId
                ? `No images to handle at ${subAreas.find(sa => sa.id === selectedSubAreaId) && subAreas.find(sa => sa.id === selectedSubAreaId).name}`
              : 'No images to handle' }}
            </h3>
          </v-flex>
        </v-layout>
      </v-container>

      <v-container v-else-if="activeImages.length || deactivatedImages.length" grid-list-xs fluid>
        <v-layout row align-center class="mb-4">
          <v-flex>
            <h3>Total unhandled images: {{ totalCount }}</h3>
          </v-flex>
        </v-layout>

        <v-layout
          v-if="deactivatedImages.length"
          row
          wrap
          class="mb-4"
        >
          <v-flex xs12>
            <h4 class="mb-2">Images marked as 'Do not import':</h4>
          </v-flex>
          <v-flex
            v-for="image in deactivatedImages"
            :key="image.identifier"
            xs4
          >
            <v-card flat tile class="deactivated-image">
              <v-img
                :src="image.imageUrl"
                :srcset="`${image.imageUrlSmall} 1x`"
                height="160px"
              >
                <v-layout class="image-controls">
                  <v-btn
                    :href="image.imageUrl"
                    color="white"
                    target="_blank"
                    icon
                    fab
                    small
                    class="view-button"
                  >
                    <v-icon>crop_original</v-icon>
                  </v-btn>
                  <div class="action-buttons">
                    <v-btn
                      color="info"
                      small
                      @click="handleRestoreImage(image.identifier)"
                    >
                      Restore
                    </v-btn>
                  </div>
                </v-layout>
              </v-img>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout v-if="activeImages.length" row wrap>
          <v-flex xs12>
            <h4 class="mb-2">Images to review:</h4>
          </v-flex>
          <v-flex
            v-for="image in activeImages"
            :key="image.identifier"
            xs4
          >
            <v-card flat tile>
              <v-img
                :src="image.imageUrl"
                :srcset="`${image.imageUrlSmall} 1x`"
                height="160px"
              >
                <v-layout class="image-controls">
                  <v-btn
                    :href="image.imageUrl"
                    color="white"
                    target="_blank"
                    icon
                    fab
                    small
                    class="view-button"
                  >
                    <v-icon>crop_original</v-icon>
                  </v-btn>
                  <div class="action-buttons">
                    <v-btn
                      color="error"
                      small
                      @click="deactivateImage(image.identifier)"
                    >
                      DO NOT IMPORT
                    </v-btn>
                  </div>
                </v-layout>
              </v-img>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout row justify-center class="mt-4">
          <v-btn
            color="primary"
            @click="updateImages"
          >
            {{ Object.values(images).length < totalCount ? 'Handle images & go to next batch of unhandled images' : 'Handle all images' }}
          </v-btn>
        </v-layout>
      </v-container>
    </v-container>
  </v-card>
</template>

<style scoped>
.min-height {
  min-height: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.no-images-message {
  min-height: 300px;
}

.image-controls {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.view-button {
  margin-left: 8px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  margin-right: 8px;
}

.deactivated-image {
  opacity: 0.7;
}
</style>



// WEBPACK FOOTER //
// src/pages/ski/skistar/SkistarResortUnhandledImages.vue